import { useState, useEffect, useCallback, useRef } from 'react';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { ScrollArea } from '../../ui/scroll-area';
import { Separator } from '../../ui/separator';
import { Check, ChevronsUpDown, Plus, Package, Search, X, Clock } from 'lucide-react';
import { cn } from '../../ui/utils';
import { procurementService, ProcurementItem, ItemCategory } from '../../../src/services/procurement.service';
import { showError } from '../../../src/utils/notification.utils';
import { authService } from '../../../src/services/auth.service';

interface ItemSearchSelectProps {
  value?: ProcurementItem | null;
  onChange: (item: ProcurementItem | null) => void;
  onAddCustomItem?: () => void;
  vendorId?: string;
  category?: ItemCategory;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function ItemSearchSelect({
  value,
  onChange,
  onAddCustomItem,
  vendorId,
  category,
  placeholder = 'Search items...',
  disabled = false,
  className
}: ItemSearchSelectProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [items, setItems] = useState<ProcurementItem[]>([]);
  const [recentItems, setRecentItems] = useState<ProcurementItem[]>([]);
  const [loading, setLoading] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();

  // Load recent items from localStorage
  useEffect(() => {
    const recent = localStorage.getItem('recent-procurement-items');
    if (recent) {
      try {
        const parsedRecent = JSON.parse(recent) as ProcurementItem[];
        setRecentItems(parsedRecent.slice(0, 5));
      } catch (error) {
        console.error('Failed to parse recent items:', error);
      }
    }
  }, []);

  // Save selected item to recent items
  const saveToRecentItems = useCallback((item: ProcurementItem) => {
    const recent = localStorage.getItem('recent-procurement-items');
    let recentArray: ProcurementItem[] = [];

    if (recent) {
      try {
        recentArray = JSON.parse(recent);
      } catch (error) {
        console.error('Failed to parse recent items:', error);
      }
    }

    // Remove if already exists
    recentArray = recentArray.filter(i => i.item_id !== item.item_id);

    // Add to front
    recentArray.unshift(item);

    // Keep only 10 recent items
    recentArray = recentArray.slice(0, 10);

    localStorage.setItem('recent-procurement-items', JSON.stringify(recentArray));
    setRecentItems(recentArray.slice(0, 5));
  }, []);

  // Debounced search
  const searchItems = useCallback(async (query: string) => {
    if (!query || query.length < 2) {
      setItems([]);
      return;
    }

    setLoading(true);
    try {
      const userProfile = await authService.getUserProfile();
      console.log('[ItemSearch] Searching items with query:', query);
      console.log('[ItemSearch] User profile:', userProfile);

      const organizationId = userProfile.organizationId;
      const filters: Record<string, string> = {};
      if (vendorId) filters.vendor_id = vendorId;
      if (category) filters.category = category;

      const response = await procurementService.searchProcurementItems(query, organizationId, filters);
      console.log('[ItemSearch] Search response:', response);

      if (response.success && response.data) {
        const itemsData = response.data;
        setItems(Array.isArray(itemsData) ? itemsData : []);
      } else {
        console.warn('[ItemSearch] No success or no data in response');
        setItems([]);
      }
    } catch (error: any) {
      console.error('[ItemSearch] Search error:', error);
      showError({
        title: 'Search Failed',
        message: error?.message || 'Failed to search procurement items'
      });
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [vendorId, category]);

  // Handle search input change with debounce
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        searchItems(searchQuery);
      }, 300);
    } else {
      setItems([]);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, searchItems]);

  const handleSelect = (item: ProcurementItem) => {
    onChange(item);
    saveToRecentItems(item);
    setOpen(false);
    setSearchQuery('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
  };

  const getCategoryLabel = (cat: string): string => {
    const labels: Record<string, string> = {
      concrete: 'Concrete',
      masonry: 'Masonry',
      metals: 'Metals',
      wood_plastics: 'Wood & Plastics',
      thermal_moisture: 'Thermal & Moisture',
      openings: 'Openings',
      finishes: 'Finishes',
      fire_suppression: 'Fire Suppression',
      plumbing: 'Plumbing',
      hvac: 'HVAC',
      electrical: 'Electrical',
      earthwork: 'Earthwork',
      exterior: 'Exterior',
      utilities: 'Utilities',
      other: 'Other'
    };
    return labels[cat] || cat;
  };

  const renderItemDisplay = (item: ProcurementItem) => (
    <div className="flex items-start gap-3 w-full">
      <div className="flex-shrink-0 mt-1">
        <Package className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm">{item.item_code}</span>
          <Badge variant="outline" className="text-xs">
            {getCategoryLabel(item.category)}
          </Badge>
        </div>
        <p className="text-sm text-foreground line-clamp-1">{item.item_name}</p>
        {item.description && (
          <p className="text-xs text-muted-foreground line-clamp-1">{item.description}</p>
        )}
        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
          <span>UOM: {item.unit_of_measure}</span>
          {item.standard_unit_price && (
            <span>Price: ${typeof item.standard_unit_price === 'number' ? item.standard_unit_price.toFixed(2) : parseFloat(item.standard_unit_price).toFixed(2)}</span>
          )}
          {item.manufacturer && (
            <span>Mfg: {item.manufacturer}</span>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn('w-full justify-between h-auto min-h-[40px]', className)}
        >
          {value ? (
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <Package className="h-4 w-4 flex-shrink-0" />
              <div className="flex-1 text-left min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm">{value.item_code}</span>
                  <span className="text-sm truncate">{value.item_name}</span>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 hover:bg-destructive hover:text-destructive-foreground"
                onClick={handleClear}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[600px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search by code, name, or description..."
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            {loading && (
              <div className="py-6 text-center text-sm text-muted-foreground">
                <Search className="h-4 w-4 animate-pulse mx-auto mb-2" />
                Searching...
              </div>
            )}

            {!loading && searchQuery.length < 2 && recentItems.length > 0 && (
              <CommandGroup heading="Recent Items">
                {recentItems.map((item) => (
                  <CommandItem
                    key={item.item_id}
                    value={item.item_id}
                    onSelect={() => handleSelect(item)}
                    className="p-3"
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4 flex-shrink-0',
                        value?.item_id === item.item_id ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    {renderItemDisplay(item)}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {!loading && searchQuery.length < 2 && recentItems.length === 0 && (
              <CommandEmpty>
                <div className="py-6 text-center">
                  <Clock className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">
                    Start typing to search items (min 2 characters)
                  </p>
                </div>
              </CommandEmpty>
            )}

            {!loading && searchQuery.length >= 2 && items.length === 0 && (
              <CommandEmpty>
                <div className="py-6 text-center">
                  <Package className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground mb-3">No items found</p>
                  {onAddCustomItem && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setOpen(false);
                        onAddCustomItem();
                      }}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Custom Item
                    </Button>
                  )}
                </div>
              </CommandEmpty>
            )}

            {!loading && items.length > 0 && (
              <CommandGroup heading={`Found ${items.length} items`}>
                <ScrollArea className="h-[400px]">
                  {items.map((item) => (
                    <CommandItem
                      key={item.item_id}
                      value={item.item_id}
                      onSelect={() => handleSelect(item)}
                      className="p-3"
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4 flex-shrink-0',
                          value?.item_id === item.item_id ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      {renderItemDisplay(item)}
                    </CommandItem>
                  ))}
                </ScrollArea>
              </CommandGroup>
            )}

            {onAddCustomItem && searchQuery.length >= 2 && (
              <>
                <Separator />
                <CommandGroup>
                  <CommandItem
                    onSelect={() => {
                      setOpen(false);
                      onAddCustomItem();
                    }}
                    className="p-3 cursor-pointer"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    <span className="font-medium">Add Custom Item</span>
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
